import { useSyncExternalStore } from "react";
import {
  defaultRivalryCalendar,
  readRivalryCalendar,
  reportingCalendarYear,
  rivalryCalendarFilters,
  writeRivalryCalendar,
  type RivalryCalendarSelection,
} from "../../../shared/presentation/rivalry-calendar.js";

let snapshot = {
  selection: defaultRivalryCalendar,
  currentYear: reportingCalendarYear(),
};
let lastSearch: string | null = null;
const listeners = new Set<() => void>();
let clockTimer: ReturnType<typeof setTimeout> | undefined;

function getSnapshot() {
  const search = typeof window === "undefined" ? "" : window.location.search;
  const currentYear = reportingCalendarYear();
  const incoming = search !== lastSearch ? readRivalryCalendar(search) : null;
  lastSearch = search;
  if (incoming || currentYear !== snapshot.currentYear)
    snapshot = { selection: incoming ?? snapshot.selection, currentYear };
  return snapshot;
}

function refresh() {
  getSnapshot();
  listeners.forEach((listener) => listener());
}

export function subscribeRivalryCalendar(listener: () => void) {
  if (!listeners.size) {
    window.addEventListener("popstate", refresh);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    scheduleClock();
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (!listeners.size) {
      window.removeEventListener("popstate", refresh);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      clearTimeout(clockTimer);
    }
  };
}

function scheduleClock() {
  const nextYear = Date.UTC(reportingCalendarYear() + 1, 0, 1);
  clockTimer = setTimeout(
    () => {
      refresh();
      scheduleClock();
    },
    Math.min(nextYear - Date.now() + 25, 86_400_000),
  );
}

export function setRivalryCalendar(selection: RivalryCalendarSelection): void {
  const url = new URL(window.location.href);
  writeRivalryCalendar(url.searchParams, selection);
  window.history.replaceState(window.history.state, "", url);
  lastSearch = url.search;
  snapshot = { selection, currentYear: reportingCalendarYear() };
  listeners.forEach((listener) => listener());
}

export function rivalryCalendarSearch(): string {
  const query = new URLSearchParams();
  writeRivalryCalendar(query, getSnapshot().selection);
  return query.toString();
}

export function useRivalryCalendar() {
  const state = useSyncExternalStore(
    subscribeRivalryCalendar,
    getSnapshot,
    getSnapshot,
  );
  return {
    ...state,
    filters: rivalryCalendarFilters(state.selection, state.currentYear),
    select: setRivalryCalendar,
  };
}
